import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { ProductItem, IngredientItem, BomItem, ConversionItem, FullMasterData } from '../types';
import { ItemDropdown, DropdownItemOption } from '../components/ui/ItemDropdown';
import {
  Coffee,
  Plus,
  Trash2,
  Save,
  RotateCcw,
  Search,
  Layers,
  Box,
  Scale,
  X,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Info,
  Minus,
  PackagePlus,
} from 'lucide-react';
import { toComparisonKey } from '../utils/textNormalizer';
import { formatUnitDisplay } from '../utils/format';
import toast from 'react-hot-toast';

type AdminTab = 'RECIPES' | 'INGREDIENTS' | 'CONVERSIONS' | 'CATEGORIES';

// Dữ liệu từ điển chuẩn cố định ban đầu từ 00_Danh_muc
const INITIAL_PRODUCT_CATEGORIES = [
  'Cà phê',
  'Trà sữa',
  'Trà hoa quả',
  'Đá xay',
  'Nước ép & Sinh tố',
  'Đồ uống đóng lon/chai',
  'Topping & Ăn vặt',
  'trà',
  'sinh tố',
  'sữa chua',
  'Khác',
];

const INITIAL_INGREDIENT_CATEGORIES = [
  'Cà phê',
  'Trà',
  'Sữa & Bơ',
  'Đường & Siro',
  'Bột & Thạch',
  'Trái cây tươi',
  'Đóng lon / chai',
  'Bao bì & Tiêu hao',
  'Kem Béo',
  'kem đặc',
  'Sữa',
  'Topping',
  'Trái cây',
  'Mứt',
  'cốt dừa',
  'Cốt trà bí đao',
  'ca cao',
  'siro',
  'Khác',
];

const INITIAL_STANDARD_UNITS = [
  'g',
  'ml',
  'quả',
  'lon',
  'chai',
  'hộp',
  'túi',
  'miếng',
  'cái',
];

const INITIAL_INWARD_UNITS = [
  'Thùng',
  'Bao',
  'Bịch',
  'Hộp',
  'Chai',
  'Lon',
  'Lốc',
  'Can',
  'Gói',
  'Kg',
  'Lít',
  'Túi',
  'Vỉ',
];

const STORAGE_KEYS = {
  PROD_CATS: 'master_dict_product_categories',
  ING_CATS: 'master_dict_ingredient_categories',
  STD_UNITS: 'master_dict_standard_units',
  INWARD_UNITS: 'master_dict_inward_units',
};

const getStoredList = (key: string, defaultList: string[]): string[] => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return defaultList;
};

export const MasterAdminScreen: React.FC = () => {
  const getSubTabFromUrl = (): AdminTab => {
    const params = new URLSearchParams(window.location.search);
    const subTab = params.get('master_tab');
    if (subTab && ['RECIPES', 'INGREDIENTS', 'CONVERSIONS', 'CATEGORIES'].includes(subTab)) {
      return subTab as AdminTab;
    }
    return 'RECIPES';
  };

  const [activeTab, setActiveTabState] = useState<AdminTab>(getSubTabFromUrl);

  const setActiveTab = (tab: AdminTab) => {
    setActiveTabState(tab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'MASTER_ADMIN');
    url.searchParams.set('master_tab', tab);
    window.history.pushState({}, '', url.toString());
  };

  useEffect(() => {
    const handlePopState = () => {
      setActiveTabState(getSubTabFromUrl());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [itemTypeFilter, setItemTypeFilter] = useState<'ALL' | 'SALES' | 'BTP'>('ALL');

  // Master State
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [ingredients, setIngredients] = useState<IngredientItem[]>([]);
  const [boms, setBoms] = useState<BomItem[]>([]);
  const [conversions, setConversions] = useState<ConversionItem[]>([]);

  // 00_Danh_muc Custom Categories State (Chủ quán có thể thêm / bớt trực tiếp và lưu bền vững)
  const [productCategories, setProductCategories] = useState<string[]>(() =>
    getStoredList(STORAGE_KEYS.PROD_CATS, INITIAL_PRODUCT_CATEGORIES)
  );
  const [ingredientCategories, setIngredientCategories] = useState<string[]>(() =>
    getStoredList(STORAGE_KEYS.ING_CATS, INITIAL_INGREDIENT_CATEGORIES)
  );
  const [standardUnits, setStandardUnits] = useState<string[]>(() =>
    getStoredList(STORAGE_KEYS.STD_UNITS, INITIAL_STANDARD_UNITS)
  );
  const [inwardUnits, setInwardUnits] = useState<string[]>(() =>
    getStoredList(STORAGE_KEYS.INWARD_UNITS, INITIAL_INWARD_UNITS)
  );

  // Sync state changes to localStorage so F5 will preserve edits
  const updateProductCategories = (newList: string[]) => {
    setProductCategories(newList);
    localStorage.setItem(STORAGE_KEYS.PROD_CATS, JSON.stringify(newList));
  };
  const updateIngredientCategories = (newList: string[]) => {
    setIngredientCategories(newList);
    localStorage.setItem(STORAGE_KEYS.ING_CATS, JSON.stringify(newList));
  };
  const updateStandardUnits = (newList: string[]) => {
    setStandardUnits(newList);
    localStorage.setItem(STORAGE_KEYS.STD_UNITS, JSON.stringify(newList));
  };
  const updateInwardUnits = (newList: string[]) => {
    setInwardUnits(newList);
    localStorage.setItem(STORAGE_KEYS.INWARD_UNITS, JSON.stringify(newList));
  };

  // Input states for adding new category items in Tab 3
  const [newCatInput, setNewCatInput] = useState<{ [key: string]: string }>({});

  // Lưu tạm sản lượng mẻ mặc định của BTP mới tạo trước khi thêm thành phần con vào boms
  const [btpPendingYieldMap, setBtpPendingYieldMap] = useState<Record<string, { qty: number; unit: string }>>(() => {
    try {
      const saved = localStorage.getItem('bar_btp_pending_yield');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const updateBtpPendingYield = (name: string, qty: number, unit?: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    const canon = toComparisonKey(trimmed);
    setBtpPendingYieldMap((prev) => {
      const existingUnit = prev[lower]?.unit || prev[canon]?.unit || unit || 'g';
      const next = {
        ...prev,
        [lower]: { qty, unit: unit || existingUnit },
        [canon]: { qty, unit: unit || existingUnit },
      };
      try {
        localStorage.setItem('bar_btp_pending_yield', JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  // Dirty tracker
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Sync isDirty with window unload & tab switch alerts + Favicon dot
  useEffect(() => {
    // 1. Favicon red dot indicator & Document Title
    const originalTitle = document.title;
    const faviconLink = document.querySelector<HTMLLinkElement>("link[rel*='icon']");

    if (isDirty) {
      document.title = `● Chưa lưu - ${originalTitle.replace(/^● Chưa lưu - /, '')}`;

      // Vẽ favicon có chấm đỏ báo hiệu
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.src = '/favicon.svg';
        img.onload = () => {
          ctx.drawImage(img, 0, 0, 64, 64);
          // Chấm tròn đỏ ở góc trên bên phải
          ctx.beginPath();
          ctx.arc(52, 12, 10, 0, 2 * Math.PI);
          ctx.fillStyle = '#EF4444';
          ctx.fill();
          ctx.lineWidth = 3;
          ctx.strokeStyle = '#FFFFFF';
          ctx.stroke();

          if (faviconLink) {
            faviconLink.href = canvas.toDataURL('image/png');
          }
        };
      }
    } else {
      document.title = originalTitle.replace(/^● Chưa lưu - /, '');
      if (faviconLink) {
        faviconLink.href = '/favicon.svg';
      }
    }

    // 2. Chặn thoát tab hoặc reload trình duyệt khi có thay đổi chưa lưu
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'Bạn có thay đổi Master Data chưa lưu. Bạn có chắc muốn rời khỏi trang không?';
        return e.returnValue;
      }
    };

    // 3. Cảnh báo khi người dùng chuyển sang tab trình duyệt khác (visibilitychange)
    const handleVisibilityChange = () => {
      if (!document.hidden && isDirty) {
        // Người dùng vừa chuyển tab trình duyệt khác rồi quay lại tab này
        setShowUnsavedModal(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.title = originalTitle.replace(/^● Chưa lưu - /, '');
      if (faviconLink) {
        faviconLink.href = '/favicon.svg';
      }
    };
  }, [isDirty]);

  // Expanded items for Tree (collapsible products)
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});

  // Highlight newly added product
  const [newlyAddedProductName, setNewlyAddedProductName] = useState<string | null>(null);
  const newProductRowRef = useRef<HTMLTableRowElement | null>(null);

  // Modals for additions
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddBtpModal, setShowAddBtpModal] = useState(false);
  const [showAddIngredientModal, setShowAddIngredientModal] = useState(false);
  const [showAddChildModal, setShowAddChildModal] = useState<{
    parentName: string;
    defaultYieldQty?: number;
    defaultYieldUnit?: string;
  } | null>(null);
  const [showAddConversionModal, setShowAddConversionModal] = useState(false);

  // Child modal state (L1 format with ItemDropdown & Stepper)
  const [childModalSelectedName, setChildModalSelectedName] = useState<string>('');
  const [childModalAmount, setChildModalAmount] = useState<number>(10);
  const [childModalUnit, setChildModalUnit] = useState<string>('g');

  // Conversion modal searchable dropdown state
  const [convModalSelectedIng, setConvModalSelectedIng] = useState<string>('');

  // Custom Delete Confirmation Modal State (Thống nhất UI, loại bỏ window.confirm)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    itemName: string;
    onConfirm: () => void;
  } | null>(null);

  // Load master data on mount
  const loadData = async (refresh = false) => {
    try {
      setLoading(true);
      const data = await api.getMasterData(refresh);
      // Sort products and ingredients by updated_at DESC when loading
      const sortedProducts = [...(data.products || [])].sort((a, b) => {
        const timeA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const timeB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return timeB - timeA;
      });
      const sortedIngredients = [...(data.ingredients || [])].sort((a, b) => {
        const timeA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const timeB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return timeB - timeA;
      });

      // Tự động chuẩn hóa nếu có nguyên liệu bị lỗi unit = 'unit' hoặc rỗng (do dữ liệu Google Sheets để trống cột ĐVT)
      const cleanedIngredients = sortedIngredients.map((ing) => {
        const u = (ing.unit || '').trim();
        return {
          ...ing,
          unit: (!u || u.toLowerCase() === 'unit') ? 'g' : u,
        };
      });

      const cleanedConversions = (data.conversions || []).map((c) => {
        const matched = cleanedIngredients.find(
          (i) => toComparisonKey(i.name) === toComparisonKey(c.ingredient_name)
        );
        const std = (c.standard_unit || '').trim();
        return {
          ...c,
          standard_unit: matched?.unit || ((!std || std.toLowerCase() === 'unit') ? 'g' : std),
        };
      });

      setProducts(sortedProducts);
      setIngredients(cleanedIngredients);
      setBoms(data.boms || []);
      setConversions(cleanedConversions);
      setIsDirty(false);

      // Expand all products by default
      const initialExpanded: Record<string, boolean> = {};
      (data.products || []).forEach((p) => {
        initialExpanded[p.name] = true;
      });
      setExpandedProducts(initialExpanded);

      // Check if user has saved custom categories in localStorage, otherwise initialize
      const storedProdCats = localStorage.getItem(STORAGE_KEYS.PROD_CATS);
      if (!storedProdCats && data.products) {
        const extraCats = new Set(INITIAL_PRODUCT_CATEGORIES);
        data.products.forEach((p) => { if (p.category) extraCats.add(p.category); });
        updateProductCategories(Array.from(extraCats));
      }
      const storedIngCats = localStorage.getItem(STORAGE_KEYS.ING_CATS);
      if (!storedIngCats && data.ingredients) {
        const extraIngCats = new Set(INITIAL_INGREDIENT_CATEGORIES);
        data.ingredients.forEach((i) => { if (i.category) extraIngCats.add(i.category); });
        updateIngredientCategories(Array.from(extraIngCats));
      }
    } catch (err: any) {
      toast.error('Lỗi tải Master Data: ' + (err?.message || 'Không thể kết nối'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Scroll to newly added product smoothly
  useEffect(() => {
    if (newlyAddedProductName && newProductRowRef.current) {
      newProductRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setNewlyAddedProductName(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [newlyAddedProductName]);

  // Set of prepared item names (BTP):
  // 1. Từ ingredients có type === 'PREPARED'
  // 2. Từ Cột A của BOM (những mục có công thức con hoặc có yield_unit !== 'Ly' / 'PORTION')
  const preparedSet = useMemo(() => {
    const set = new Set<string>();
    // Thêm các BTP được khai báo trong danh mục nguyên liệu
    ingredients.forEach((ing) => {
      if (ing.type === 'PREPARED') {
        set.add(ing.name.toLowerCase().trim());
      }
    });

    // Thêm các BTP xuất hiện trong BOM mà không phải món bán thông thường
    const prodSet = new Set(products.map((p) => p.name.toLowerCase().trim()));
    boms.forEach((b) => {
      const parentLower = b.parent_name.toLowerCase().trim();
      // Nếu không phải món bán hoặc có yield_unit là 'g' / 'ml'
      if (!prodSet.has(parentLower) || (b.yield_unit && b.yield_unit.toLowerCase() !== 'ly' && b.yield_unit.toLowerCase() !== 'portion')) {
        set.add(parentLower);
      }
    });
    return set;
  }, [products, boms, ingredients]);

  // Map parent_name -> child BOM items (hỗ trợ case-insensitive và canonical comparison lookup)
  const bomsByParent = useMemo(() => {
    const map = new Map<string, BomItem[]>();
    for (const b of boms) {
      const parent = b.parent_name.trim();
      const parentLower = parent.toLowerCase();
      const parentCanonical = toComparisonKey(parent);
      
      const list = map.get(parent) || [];
      list.push(b);
      map.set(parent, list);

      if (parent !== parentLower) {
        const listLower = map.get(parentLower) || [];
        listLower.push(b);
        map.set(parentLower, listLower);
      }

      if (parentCanonical && parentCanonical !== parentLower && parentCanonical !== parent) {
        const listCanon = map.get(parentCanonical) || [];
        listCanon.push(b);
        map.set(parentCanonical, listCanon);
      }
    }
    return map;
  }, [boms]);

  // Helper function để lấy danh sách BOM con của 1 món / BTP bất kể viết hoa thường hay dấu cách
  const getBomChildren = (name: string): BomItem[] => {
    if (!name) return [];
    return (
      bomsByParent.get(name) ||
      bomsByParent.get(name.trim()) ||
      bomsByParent.get(name.trim().toLowerCase()) ||
      bomsByParent.get(toComparisonKey(name)) ||
      []
    );
  };

  // Unit lookup for ingredients & BTP
  const unitMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const ing of ingredients) {
      map.set(ing.name.toLowerCase(), ing.unit || 'g');
    }
    return map;
  }, [ingredients]);

  /* =========================================================================
     CỘT F CỦA 00_Danh_muc: Thành phần con cho Cột D 03_Dinh_muc
     Gồm: Nguyên liệu thô (từ kho 02) + Bán thành phẩm (từ BOM)
     Được format chuẩn DropdownItemOption cho component ItemDropdown của L1
  ========================================================================= */
  const dropdownItemsForBom = useMemo<DropdownItemOption[]>(() => {
    const list: DropdownItemOption[] = [];

    // 1. Thêm các Bán thành phẩm (BTP)
    const btpNames = Array.from(preparedSet);
    for (const btp of btpNames) {
      const originalName = boms.find((b) => b.parent_name.toLowerCase() === btp)?.parent_name || btp;
      list.push({
        key: `btp-${originalName}`,
        name: originalName,
        category: '⭐ Bán Thành Phẩm (BTP)',
        type: 'PREPARED',
        unit: 'g',
        displayName: `[BTP] ${originalName}`,
      });
    }

    // 2. Thêm các Nguyên liệu thô trong kho
    for (const ing of ingredients) {
      list.push({
        key: `raw-${ing.name}`,
        name: ing.name,
        category: ing.category || '📦 Nguyên Liệu Thô',
        type: 'RAW',
        unit: ing.unit || 'g',
        displayName: `${ing.name} (${ing.unit})`,
      });
    }

    return list;
  }, [preparedSet, boms, ingredients]);

  // Dropdown list specifically for 04_Quy_doi (RAW materials only) with search & category support
  const dropdownItemsForRaw = useMemo<DropdownItemOption[]>(() => {
    return ingredients
      .filter((ing) => ing.type !== 'PREPARED')
      .map((ing) => ({
        key: `raw-only-${ing.name}`,
        name: ing.name,
        category: ing.category || '📦 Nguyên Liệu Thô',
        type: 'RAW',
        unit: ing.unit || 'g',
        displayName: `${ing.name} (ĐVT chuẩn: ${ing.unit})`,
      }));
  }, [ingredients]);

  // Save Master Data to D1 & KV
  const handleSaveMaster = async () => {
    try {
      setSaving(true);
      const payload: FullMasterData = {
        products,
        ingredients,
        boms,
        conversions,
      };
      const res = await api.saveMasterData(payload);
      if (res.success) {
        toast.success(res.message || 'Đã lưu Master Data thành công vào hệ thống!');
        setIsDirty(false);
      } else {
        toast.error('Lưu thất bại: ' + (res as any).error);
      }
    } catch (err: any) {
      toast.error('Lỗi khi lưu: ' + (err?.message || 'Không thể kết nối'));
    } finally {
      setSaving(false);
    }
  };

  // Toggle tree expansion
  const toggleExpand = (name: string) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  // Delete product with custom modal
  const handleDeleteProduct = (productName: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      title: 'Xóa Món Khỏi Menu',
      message: `Bạn có chắc muốn xóa món "${productName}"? Toàn bộ định mức công thức liên quan của món này cũng sẽ bị xóa.`,
      itemName: productName,
      onConfirm: () => {
        setProducts((prev) => prev.filter((p) => p.name !== productName));
        setBoms((prev) => prev.filter((b) => b.parent_name !== productName));
        setIsDirty(true);
        setDeleteConfirmModal(null);
        toast.success(`Đã xóa món ${productName}`);
      },
    });
  };

  // Delete BOM child item with custom modal
  const handleDeleteBomChild = (parentName: string, ingredientName: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      title: 'Xóa Thành Phần Khỏi Món',
      message: `Xác nhận xóa thành phần "${ingredientName}" khỏi công thức của "${parentName}"?`,
      itemName: `${parentName} → ${ingredientName}`,
      onConfirm: () => {
        const parentCanon = toComparisonKey(parentName);
        const childCanon = toComparisonKey(ingredientName);
        setBoms((prev) =>
          prev.filter(
            (b) =>
              !(
                (toComparisonKey(b.parent_name) === parentCanon || b.parent_name === parentName) &&
                (toComparisonKey(b.ingredient_name) === childCanon || b.ingredient_name === ingredientName)
              )
          )
        );
        setIsDirty(true);
        setDeleteConfirmModal(null);
        toast.success(`Đã xóa thành phần "${ingredientName}" khỏi "${parentName}"`);
      },
    });
  };

  // Update BOM amount inline
  const handleUpdateBomAmount = (parentName: string, ingredientName: string, newAmount: number) => {
    if (isNaN(newAmount) || newAmount < 0) return;
    const parentCanon = toComparisonKey(parentName);
    const childCanon = toComparisonKey(ingredientName);
    setBoms((prev) =>
      prev.map((b) => {
        const matchParent = toComparisonKey(b.parent_name) === parentCanon || b.parent_name === parentName;
        const matchChild = toComparisonKey(b.ingredient_name) === childCanon || b.ingredient_name === ingredientName;
        if (matchParent && matchChild) {
          return { ...b, amount: newAmount };
        }
        return b;
      })
    );
    setIsDirty(true);
  };

  // Update Product Price inline (giữ nguyên vị trí trong bảng, cập nhật updated_at)
  const handleUpdateProductPrice = (productName: string, newPrice: number) => {
    if (isNaN(newPrice) || newPrice < 0) return;
    const nowIso = new Date().toISOString();
    setProducts((prev) =>
      prev.map((p) => (p.name === productName ? { ...p, price: newPrice, updated_at: nowIso } : p))
    );
    setIsDirty(true);
  };

  // Update Product Category (Dropdown from 00_Danh_muc)
  const handleUpdateProductCategory = (productName: string, newCategory: string) => {
    const nowIso = new Date().toISOString();
    setProducts((prev) =>
      prev.map((p) => (p.name === productName ? { ...p, category: newCategory, updated_at: nowIso } : p))
    );
    setIsDirty(true);
  };

  // Update Product Status (Dropdown [ACTIVE, INACTIVE])
  const handleUpdateProductStatus = (productName: string, newStatus: 'ACTIVE' | 'INACTIVE') => {
    const nowIso = new Date().toISOString();
    setProducts((prev) =>
      prev.map((p) => (p.name === productName ? { ...p, status: newStatus, updated_at: nowIso } : p))
    );
    setIsDirty(true);
  };

  // Add new Product: PREPEND LÊN ĐẦU DANH SÁCH + TỰ ĐỘNG FOCUS & HIGHLIGHT
  const handleCreateProduct = (name: string, category: string, price: number) => {
    if (!name.trim()) return;
    if (products.some((p) => p.name.toLowerCase() === name.trim().toLowerCase())) {
      toast.error(`Món "${name}" đã tồn tại!`);
      return;
    }

    const newProd: ProductItem = {
      name: name.trim(),
      category: category || productCategories[0] || 'Cà phê',
      price: price || 0,
      status: 'ACTIVE',
      updated_at: new Date().toISOString(),
    };

    // Prepend lên đầu danh sách
    setProducts((prev) => [newProd, ...prev]);
    setExpandedProducts((prev) => ({ ...prev, [newProd.name]: true }));
    setNewlyAddedProductName(newProd.name);
    setIsDirty(true);
    setShowAddProductModal(false);
    toast.success(`Đã thêm món mới: "${newProd.name}" lên đầu danh sách!`, {
      icon: '✨',
      duration: 3500,
    });
  };

  // Add new BTP (Bán thành phẩm) - tự động đăng ký vào ingredients type PREPARED & mở rộng BOM
  const handleCreateBtp = (name: string, yieldQty: number, yieldUnit: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    const canon = toComparisonKey(trimmedName);
    if (ingredients.some((i) => toComparisonKey(i.name) === canon || i.name.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error(`Bán thành phẩm hoặc nguyên liệu "${trimmedName}" đã tồn tại!`);
      return;
    }

    const newBtpIng: IngredientItem = {
      name: trimmedName,
      category: 'Bán thành phẩm',
      type: 'PREPARED',
      unit: yieldUnit || 'g',
      safety_stock: 0,
      safety_stock_store: 0,
      safety_stock_central: 0,
      loss_tolerance_pct: 10,
      updated_at: new Date().toISOString(),
    };

    setIngredients((prev) => [newBtpIng, ...prev]);
    updateBtpPendingYield(trimmedName, yieldQty, yieldUnit || 'g');
    setIsDirty(true);
    setShowAddBtpModal(false);
    // Mở ngay modal thêm thành phần con đầu tiên cho BTP này, truyền kèm yieldQty và yieldUnit đã chọn
    setShowAddChildModal({ parentName: trimmedName, defaultYieldQty: yieldQty, defaultYieldUnit: yieldUnit });
    setChildModalSelectedName('');
    setChildModalAmount(10);
    setChildModalUnit('g');

    toast.success(`Đã tạo BTP "${trimmedName}" (Sản lượng mẻ: ${yieldQty} ${yieldUnit})! Hãy chọn thành phần con.`, {
      icon: '⭐',
      duration: 4000,
    });
  };

  // Add child to BOM using ItemDropdown
  const handleAddChildToBomConfirm = () => {
    if (!showAddChildModal) return;
    const parentName = showAddChildModal.parentName;
    const childName = childModalSelectedName.trim();
    const amount = Number(childModalAmount);

    if (!childName || isNaN(amount) || amount <= 0) {
      toast.error('Vui lòng chọn thành phần từ danh sách và nhập định mức > 0');
      return;
    }
    const parentCanon = toComparisonKey(parentName);
    const childCanon = toComparisonKey(childName);
    const exists = boms.some(
      (b) => toComparisonKey(b.parent_name) === parentCanon && toComparisonKey(b.ingredient_name) === childCanon
    );
    if (exists) {
      toast.error(`"${childName}" đã có sẵn trong công thức của "${parentName}"`);
      return;
    }

    // Nếu parent là BTP, lấy yield_quantity & yield_unit từ defaultYieldQty/Unit (nếu vừa tạo), hoặc BOM có sẵn của nó, hoặc btpPendingYieldMap, hoặc fallback 1000g
    const existingParentBom = boms.find((b) => toComparisonKey(b.parent_name) === parentCanon || b.parent_name.toLowerCase() === parentName.toLowerCase());
    const isParentBtp = preparedSet.has(parentName.toLowerCase()) || preparedSet.has(parentCanon) || ingredients.some(i => i.type === 'PREPARED' && toComparisonKey(i.name) === parentCanon);
    const parentIng = ingredients.find((i) => toComparisonKey(i.name) === parentCanon || i.name.toLowerCase() === parentName.toLowerCase());
    const pendingYield = btpPendingYieldMap[parentCanon] || btpPendingYieldMap[parentName.toLowerCase().trim()];
    
    const yieldQty = showAddChildModal.defaultYieldQty ?? (existingParentBom ? existingParentBom.yield_quantity : (pendingYield ? pendingYield.qty : (isParentBtp ? 1000 : 1)));
    const yieldUnit = showAddChildModal.defaultYieldUnit ?? (existingParentBom ? existingParentBom.yield_unit : (pendingYield ? pendingYield.unit : (isParentBtp ? (parentIng?.unit || 'g') : 'Ly')));

    const newBom: BomItem = {
      parent_name: parentName,
      yield_quantity: yieldQty,
      yield_unit: yieldUnit,
      ingredient_name: childName,
      amount: amount,
    };
    setBoms((prev) => [...prev, newBom]);
    setIsDirty(true);
    setShowAddChildModal(null);
    toast.success(`Đã thêm ${childName} (${amount} ${childModalUnit}) vào "${parentName}"`);
  };

  // Thêm bớt danh mục chuẩn trong Tab 3
  const handleAddCategoryItem = (type: 'PROD_CAT' | 'ING_CAT' | 'STD_UNIT' | 'INWARD_UNIT') => {
    const val = (newCatInput[type] || '').trim();
    if (!val) return;

    if (type === 'PROD_CAT') {
      if (!productCategories.includes(val)) {
        updateProductCategories([...productCategories, val]);
        setIsDirty(true);
        toast.success(`Đã thêm nhóm món: "${val}"`);
      }
    } else if (type === 'ING_CAT') {
      if (!ingredientCategories.includes(val)) {
        updateIngredientCategories([...ingredientCategories, val]);
        setIsDirty(true);
        toast.success(`Đã thêm nhóm hàng: "${val}"`);
      }
    } else if (type === 'STD_UNIT') {
      if (!standardUnits.includes(val)) {
        updateStandardUnits([...standardUnits, val]);
        setIsDirty(true);
        toast.success(`Đã thêm ĐVT tiêu chuẩn: "${val}"`);
      }
    } else if (type === 'INWARD_UNIT') {
      if (!inwardUnits.includes(val)) {
        updateInwardUnits([...inwardUnits, val]);
        setIsDirty(true);
        toast.success(`Đã thêm ĐVT nhập NCC: "${val}"`);
      }
    }
    setNewCatInput((prev) => ({ ...prev, [type]: '' }));
  };

  const handleRemoveCategoryItem = (type: 'PROD_CAT' | 'ING_CAT' | 'STD_UNIT' | 'INWARD_UNIT', item: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      title: 'Xóa Mục Danh Mục Chuẩn',
      message: `Bạn có chắc muốn xóa "${item}" khỏi danh mục chuẩn?`,
      itemName: item,
      onConfirm: () => {
        if (type === 'PROD_CAT') updateProductCategories(productCategories.filter((x) => x !== item));
        else if (type === 'ING_CAT') updateIngredientCategories(ingredientCategories.filter((x) => x !== item));
        else if (type === 'STD_UNIT') updateStandardUnits(standardUnits.filter((x) => x !== item));
        else if (type === 'INWARD_UNIT') updateInwardUnits(inwardUnits.filter((x) => x !== item));

        setIsDirty(true);
        setDeleteConfirmModal(null);
        toast.success(`Đã xóa "${item}"`);
      },
    });
  };

  // Danh sách các mục hiển thị ở Cột A: Món bán + Bán thành phẩm (BTP)
  const allHierarchyParents = useMemo(() => {
    // 1. Món bán thông thường
    const list: Array<{
      name: string;
      category: string;
      price: number;
      status: 'ACTIVE' | 'INACTIVE';
      isBtp: boolean;
      yieldQty?: number;
      yieldUnit?: string;
    }> = products.map((p) => ({
      name: p.name,
      category: p.category,
      price: p.price || 0,
      status: (p.status as any) || 'ACTIVE',
      isBtp: false,
    }));

    // 2. Các BTP có trong ingredients (type PREPARED) hoặc có BOM riêng
    const prodNamesLower = new Set(products.map((p) => p.name.toLowerCase().trim()));
    const prodNamesCanon = new Set(products.map((p) => toComparisonKey(p.name)));
    const btpItems: typeof list = [];

    // Tìm tất cả BTP từ preparedSet hoặc ingredients type PREPARED
    const seenBtp = new Set<string>();
    for (const btpNameLower of preparedSet) {
      const btpCanon = toComparisonKey(btpNameLower);
      if (prodNamesLower.has(btpNameLower) || prodNamesCanon.has(btpCanon) || seenBtp.has(btpCanon)) continue;
      seenBtp.add(btpCanon);

      const btpBom = boms.find(
        (b) => toComparisonKey(b.parent_name) === btpCanon || b.parent_name.toLowerCase().trim() === btpNameLower
      );
      const btpIng = ingredients.find(
        (i) => toComparisonKey(i.name) === btpCanon || i.name.toLowerCase().trim() === btpNameLower
      );
      const pendingYield = btpPendingYieldMap[btpCanon] || btpPendingYieldMap[btpNameLower];
      const originalName = btpBom?.parent_name || btpIng?.name || btpNameLower;

      const effectiveYieldQty = btpBom?.yield_quantity ?? (pendingYield?.qty ?? 1000);
      const effectiveYieldUnit = btpBom?.yield_unit ?? (pendingYield?.unit ?? (btpIng?.unit || 'g'));

      btpItems.push({
        name: originalName,
        category: '⭐ Bán Thành Phẩm (BTP)',
        price: 0,
        status: 'ACTIVE',
        isBtp: true,
        yieldQty: effectiveYieldQty,
        yieldUnit: effectiveYieldUnit,
      });
    }

    // Đặt các BTP lên đầu hoặc sau sản phẩm
    return [...list, ...btpItems];
  }, [products, boms, ingredients, preparedSet, btpPendingYieldMap]);

  // Counts for Segmented Switcher (Món bán vs BTP)
  const { salesCount, btpCount } = useMemo(() => {
    let sales = 0;
    let btp = 0;
    for (const p of allHierarchyParents) {
      if (p.isBtp) btp++;
      else sales++;
    }
    return { salesCount: sales, btpCount: btp };
  }, [allHierarchyParents]);

  // Filter items by search, category, and item type (Món bán / BTP)
  const filteredProducts = useMemo(() => {
    return allHierarchyParents.filter((p) => {
      const matchSearch =
        !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        getBomChildren(p.name).some((b) =>
          b.ingredient_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      const matchCat =
        selectedCategory === 'ALL' ||
        p.category === selectedCategory ||
        (selectedCategory === '⭐ Bán Thành Phẩm (BTP)' && p.isBtp);
      const matchType =
        itemTypeFilter === 'ALL' ||
        (itemTypeFilter === 'SALES' && !p.isBtp) ||
        (itemTypeFilter === 'BTP' && p.isBtp);
      return matchSearch && matchCat && matchType;
    });
  }, [allHierarchyParents, searchQuery, selectedCategory, itemTypeFilter, bomsByParent]);

  if (loading) {
    return (
      <div className="admin-loading-container">
        <div className="spinner-amber" />
        <p className="admin-loading-text">Đang tải toàn bộ dữ liệu Master Data...</p>
      </div>
    );
  }

  return (
    <div className="master-admin-root">
      {/* Top Header Bar */}
      <header className="master-admin-header">
        <div className="header-brand-group">
          <div className="master-badge-icon">
            <Coffee size={22} />
          </div>
          <div>
            <h1 className="master-title">QUẢN TRỊ MASTER MENU & KHO</h1>
            <p className="master-subtitle">Nguồn chuẩn dữ liệu trung tâm thay thế hoàn toàn Google Sheets</p>
          </div>
        </div>

        <div className="header-actions">
          {isDirty && (
            <span className="unsaved-badge">
              <AlertTriangle size={14} /> Có thay đổi chưa lưu
            </span>
          )}
          <button
            onClick={() => loadData(true)}
            className="btn-admin-secondary"
            title="Tải lại từ Database"
          >
            <RotateCcw size={16} />
            <span>Tải lại</span>
          </button>
          <button
            onClick={handleSaveMaster}
            disabled={saving}
            className={`btn-admin-primary ${isDirty ? 'pulse-btn' : ''}`}
          >
            {saving ? (
              <>
                <div className="btn-spinner" />
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>LƯU MASTER DATA</span>
              </>
            )}
          </button>
        </div>
      </header>

      {/* Main Tabs */}
      <div className="master-tabs-bar">
        <button
          onClick={() => setActiveTab('RECIPES')}
          className={`master-tab-btn ${activeTab === 'RECIPES' ? 'active' : ''}`}
        >
          <Layers size={17} />
          <span>1. Công Thức & Menu ({products.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('INGREDIENTS')}
          className={`master-tab-btn ${activeTab === 'INGREDIENTS' ? 'active' : ''}`}
        >
          <Box size={17} />
          <span>2. Kho Nguyên Liệu ({ingredients.filter((i) => i.type !== 'PREPARED').length})</span>
        </button>
        <button
          onClick={() => setActiveTab('CONVERSIONS')}
          className={`master-tab-btn ${activeTab === 'CONVERSIONS' ? 'active' : ''}`}
        >
          <PackagePlus size={17} />
          <span>3. Quy Đổi Đóng Gói ({conversions.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('CATEGORIES')}
          className={`master-tab-btn ${activeTab === 'CATEGORIES' ? 'active' : ''}`}
        >
          <Scale size={17} />
          <span>4. Danh Mục Chuẩn</span>
        </button>
      </div>

      {/* TAB 1: CÔNG THỨC & MENU ĐA CẤP (BÁM SÁT ẢNH EXCEL CỦA USER) */}
      {activeTab === 'RECIPES' && (
        <div className="admin-content-area">
          {/* Controls Bar */}
          <div className="filter-controls-row">
            <div className="search-box-wrapper">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Tìm tên món, BTP, hoặc nguyên liệu thành phần..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="category-select-wrapper">
              <span className="cat-label">Nhóm món (00 Col A):</span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="admin-select"
              >
                <option value="ALL">Tất cả nhóm ({products.length})</option>
                {productCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Segmented Filter: Tất cả | Món bán | Bán thành phẩm */}
            <div className="segmented-type-filter" role="group" aria-label="Lọc loại sản phẩm">
              <button
                type="button"
                onClick={() => setItemTypeFilter('ALL')}
                className={`segmented-filter-btn ${itemTypeFilter === 'ALL' ? 'active-all' : ''}`}
                title="Hiển thị toàn bộ món bán và bán thành phẩm"
              >
                <span>Tất cả</span>
                <span className="filter-count-badge">{allHierarchyParents.length}</span>
              </button>

              <button
                type="button"
                onClick={() => setItemTypeFilter('SALES')}
                className={`segmented-filter-btn ${itemTypeFilter === 'SALES' ? 'active-sales' : ''}`}
                title="Chỉ hiển thị các món đồ uống / thành phẩm bán cho khách"
              >
                <span>☕ Món bán</span>
                <span className="filter-count-badge">{salesCount}</span>
              </button>

              <button
                type="button"
                onClick={() => setItemTypeFilter('BTP')}
                className={`segmented-filter-btn ${itemTypeFilter === 'BTP' ? 'active-btp' : ''}`}
                title="Chỉ hiển thị các Bán thành phẩm (công thức mẻ ủ, nấu, pha sẵn)"
              >
                <span className="prep-pill" style={{ fontSize: 9, padding: '1px 4px' }}>BTP</span>
                <span>Bán thành phẩm</span>
                <span className="filter-count-badge">{btpCount}</span>
              </button>
            </div>

            <div className="action-buttons-group" style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowAddBtpModal(true)}
                className="btn-create-accent"
                style={{ background: 'var(--amber-700, #b45309)' }}
                title="Thêm Bán Thành Phẩm mới (Công thức mẻ ủ/nấu: Cốt trà, Cà phê phin, Trân châu...)"
              >
                <Plus size={16} />
                <span>Thêm BTP Mới</span>
              </button>
              <button
                onClick={() => setShowAddProductModal(true)}
                className="btn-create-accent"
                title="Thêm món bán mới lên đầu bảng"
              >
                <Plus size={16} />
                <span>Thêm Món Bán Mới</span>
              </button>
            </div>
          </div>

          {/* SPREADSHEET-LIKE HIERARCHY GRID */}
          <div className="spreadsheet-card">
            <div className="spreadsheet-table-container">
              <table className="excel-hierarchy-table">
                <thead>
                  <tr>
                    <th style={{ width: '28%' }} className="col-header border-r">
                      Cột A: Món Bán / BTP Cha
                    </th>
                    <th style={{ width: '26%' }} className="col-header border-r">
                      Cột B: BTP / NL Trực Tiếp
                    </th>
                    <th style={{ width: '22%' }} className="col-header border-r">
                      Cột C: Thành Phần Của BTP
                    </th>
                    <th style={{ width: '12%' }} className="col-header text-right border-r">
                      ĐLượng / Giá
                    </th>
                    <th style={{ width: '6%' }} className="col-header text-center border-r">
                      ĐVT
                    </th>
                    <th style={{ width: '6%' }} className="col-header text-center">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state-cell">
                        Không tìm thấy món nào phù hợp với bộ lọc.
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((prod) => {
                      const isExpanded = expandedProducts[prod.name] ?? true;
                      const level1Children = getBomChildren(prod.name);
                      const isNewlyAdded = newlyAddedProductName === prod.name;

                      return (
                        <React.Fragment key={`prod-${prod.name}`}>
                          {/* PRODUCT HEADER ROW (CỘT A) */}
                          <tr
                            ref={isNewlyAdded ? newProductRowRef : null}
                            className={`excel-product-row ${isNewlyAdded ? 'row-newly-added' : ''}`}
                          >
                            <td className="cell-product-name border-r">
                              <div className="product-title-flex">
                                <button
                                  onClick={() => toggleExpand(prod.name)}
                                  className="tree-toggle-btn"
                                  title={isExpanded ? 'Thu gọn' : 'Mở rộng'}
                                >
                                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                </button>

                                {prod.isBtp ? (
                                  <span className="prep-pill" style={{ fontSize: 11, padding: '2px 6px', marginRight: 4 }}>BTP</span>
                                ) : null}

                                <span className={`product-text ${prod.isBtp ? 'font-prep' : ''}`}>{prod.name}</span>
                                
                                {isNewlyAdded && (
                                  <span className="badge-new-pill">MỚI TẠO</span>
                                )}

                                {/* Nhóm món Dropdown từ 00_Danh_muc Col A */}
                                {!prod.isBtp ? (
                                  <select
                                    value={prod.category}
                                    onChange={(e) => handleUpdateProductCategory(prod.name, e.target.value)}
                                    className="cat-dropdown-select"
                                    title="Chọn Nhóm Món từ '00_Danh_muc'!A2:A"
                                  >
                                    {productCategories.map((cat) => (
                                      <option key={cat} value={cat}>
                                        {cat}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span style={{ fontSize: 11, color: 'var(--amber-700, #b45309)', fontWeight: 600, background: 'rgba(217,119,6,0.1)', padding: '2px 8px', borderRadius: 4 }}>
                                    Bán thành phẩm
                                  </span>
                                )}

                                {/* Trạng thái Dropdown từ 01_San_pham Col D (chỉ cho món bán) */}
                                {!prod.isBtp && (
                                  <select
                                    value={prod.status || 'ACTIVE'}
                                    onChange={(e) =>
                                      handleUpdateProductStatus(
                                        prod.name,
                                        e.target.value as 'ACTIVE' | 'INACTIVE'
                                      )
                                    }
                                    className={`status-dropdown-select ${prod.status === 'INACTIVE' ? 'status-inactive' : 'status-active'}`}
                                    title="Trạng thái món (Đang bán / Tạm ngưng)"
                                  >
                                    <option value="ACTIVE">Đang bán</option>
                                    <option value="INACTIVE">Tạm ngưng</option>
                                  </select>
                                )}
                              </div>
                            </td>
                            {/* Empty spacer cells B & C */}
                            <td className="cell-empty border-r" />
                            <td className="cell-empty border-r" />
                            {/* Price or Yield in column D */}
                            <td className="cell-price text-right border-r">
                              <div className="inline-edit-wrapper">
                                {!prod.isBtp ? (
                                  <input
                                    type="number"
                                    value={prod.price || 0}
                                    onChange={(e) =>
                                      handleUpdateProductPrice(prod.name, parseFloat(e.target.value) || 0)
                                    }
                                    className="inline-price-input"
                                    title="Click để sửa giá bán trực tiếp"
                                  />
                                ) : (
                                  <input
                                    type="number"
                                    value={prod.yieldQty !== undefined ? prod.yieldQty : ''}
                                    placeholder="1000"
                                    onChange={(e) => {
                                      const rawVal = e.target.value;
                                      const newYield = rawVal === '' ? 0 : parseFloat(rawVal);
                                      const prodCanon = toComparisonKey(prod.name);

                                      // 1. Luôn cập nhật vào pending map (kể cả khi chưa có BOM con)
                                      updateBtpPendingYield(prod.name, newYield, prod.yieldUnit || 'g');

                                      // 2. Cập nhật tất cả các BOM items hiện có của BTP này
                                      setBoms((prev) => {
                                        const hasBom = prev.some(
                                          (b) => toComparisonKey(b.parent_name) === prodCanon || b.parent_name.toLowerCase() === prod.name.toLowerCase()
                                        );
                                        if (!hasBom) return prev;
                                        return prev.map((b) =>
                                          toComparisonKey(b.parent_name) === prodCanon || b.parent_name.toLowerCase() === prod.name.toLowerCase()
                                            ? { ...b, yield_quantity: newYield }
                                            : b
                                        );
                                      });
                                      setIsDirty(true);
                                    }}
                                    className="inline-price-input font-mono"
                                    title="Sản lượng mẻ ủ/nấu thu được (Yield Quantity)"
                                  />
                                )}
                              </div>
                            </td>
                            <td className="cell-unit text-center border-r">
                              <span className="unit-tag">{prod.isBtp ? formatUnitDisplay(prod.yieldUnit || 'g') : 'đ'}</span>
                            </td>
                            <td className="cell-actions text-center">
                              <div className="row-action-btns">
                                <button
                                  onClick={() => {
                                    setShowAddChildModal({
                                      parentName: prod.name,
                                      defaultYieldQty: prod.yieldQty,
                                      defaultYieldUnit: prod.yieldUnit,
                                    });
                                    setChildModalSelectedName('');
                                    setChildModalAmount(10);
                                    setChildModalUnit('g');
                                  }}
                                  className="action-icon-btn add-btn"
                                  title={`Thêm thành phần cho ${prod.name}`}
                                >
                                  <Plus size={15} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (prod.isBtp) {
                                      setDeleteConfirmModal({
                                        isOpen: true,
                                        title: 'Xóa Bán Thành Phẩm',
                                        message: `Bạn có chắc muốn xóa BTP "${prod.name}" và toàn bộ công thức của nó?`,
                                        itemName: prod.name,
                                        onConfirm: () => {
                                          const prodCanon = toComparisonKey(prod.name);
                                          setIngredients((prev) => prev.filter((i) => toComparisonKey(i.name) !== prodCanon && i.name !== prod.name));
                                          setBoms((prev) => prev.filter((b) => toComparisonKey(b.parent_name) !== prodCanon && toComparisonKey(b.ingredient_name) !== prodCanon));
                                          setBtpPendingYieldMap((prev) => {
                                            const next = { ...prev };
                                            delete next[prod.name.toLowerCase().trim()];
                                            delete next[prodCanon];
                                            try { localStorage.setItem('bar_btp_pending_yield', JSON.stringify(next)); } catch {}
                                            return next;
                                          });
                                          setIsDirty(true);
                                          setDeleteConfirmModal(null);
                                          toast.success(`Đã xóa BTP ${prod.name}`);
                                        },
                                      });
                                    } else {
                                      handleDeleteProduct(prod.name);
                                    }
                                  }}
                                  className="action-icon-btn delete-btn"
                                  title={`Xóa ${prod.isBtp ? 'BTP' : 'món'} ${prod.name}`}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* CHILDREN ROWS (LEVEL 1 & LEVEL 2) */}
                          {isExpanded &&
                            level1Children.map((lvl1) => {
                              const lvl1NameTrim = lvl1.ingredient_name.trim();
                              const isLvl1Prep = preparedSet.has(lvl1NameTrim.toLowerCase());
                              const level2Children = isLvl1Prep
                                ? getBomChildren(lvl1NameTrim)
                                : [];
                              const lvl1Unit = unitMap.get(lvl1NameTrim.toLowerCase()) || 'g';

                              return (
                                <React.Fragment key={`bom-${prod.name}-${lvl1.ingredient_name}`}>
                                  {/* ROW FOR LEVEL 1 CHILD (CỘT B) */}
                                  <tr className="excel-level1-row">
                                    {/* Indent under Col A */}
                                    <td className="cell-indent border-r" />
                                    {/* Content in Col B */}
                                    <td className="cell-level1-content border-r">
                                      <div className="child-item-flex">
                                        <span className="tree-branch-symbol">↳</span>
                                        {isLvl1Prep ? (
                                          <span className="prep-pill">BTP</span>
                                        ) : (
                                          <span className="raw-dot">•</span>
                                        )}
                                        <span className={`child-name ${isLvl1Prep ? 'font-prep' : ''}`}>
                                          {lvl1.ingredient_name}
                                        </span>
                                      </div>
                                    </td>
                                    {/* Col C empty */}
                                    <td className="cell-empty border-r" />
                                    {/* Amount */}
                                    <td className="cell-amount text-right border-r">
                                      <input
                                        type="number"
                                        step="any"
                                        value={lvl1.amount}
                                        onChange={(e) =>
                                          handleUpdateBomAmount(
                                            prod.name,
                                            lvl1.ingredient_name,
                                            parseFloat(e.target.value) || 0
                                          )
                                        }
                                        className="inline-amount-input"
                                        title="Sửa định mức mẻ"
                                      />
                                    </td>
                                    <td className="cell-unit text-center border-r">
                                      <span className="unit-label">{formatUnitDisplay(lvl1Unit)}</span>
                                    </td>
                                    <td className="cell-actions text-center" style={{ minWidth: prod.isBtp ? 170 : undefined }}>
                                      <div className="row-action-btns" style={{ flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                                        {prod.isBtp && (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const currentNote = lvl1.note || '';
                                                const isSub = currentNote.toUpperCase().includes('SUBSTITUTE_MEMBER');
                                                const newNote = isSub
                                                  ? currentNote.replace(/SUBSTITUTE_MEMBER/gi, '').replace(/THRESHOLD[:=]\s*\d+/gi, '').trim()
                                                  : (currentNote ? `${currentNote} SUBSTITUTE_MEMBER THRESHOLD:25000` : 'SUBSTITUTE_MEMBER THRESHOLD:25000');
                                                setBoms((prev) =>
                                                  prev.map((b) =>
                                                    toComparisonKey(b.parent_name) === toComparisonKey(prod.name) &&
                                                    toComparisonKey(b.ingredient_name) === toComparisonKey(lvl1.ingredient_name)
                                                      ? { ...b, note: newNote }
                                                      : b
                                                  )
                                                );
                                                setIsDirty(true);
                                                toast.success(
                                                  !isSub
                                                    ? `Đã bật cơ chế bù trừ thay thế cho "${lvl1.ingredient_name}" trong BTP "${prod.name}" (ngưỡng 25.000đ)`
                                                    : `Đã tắt cơ chế bù trừ thay thế cho "${lvl1.ingredient_name}" trong BTP "${prod.name}"`
                                                );
                                              }}
                                              className="action-icon-btn"
                                              style={{
                                                fontSize: '10px',
                                                padding: '2px 5px',
                                                borderRadius: '4px',
                                                backgroundColor: (lvl1.note || '').toUpperCase().includes('SUBSTITUTE_MEMBER')
                                                  ? '#FEF3C7'
                                                  : '#F3F4F6',
                                                color: (lvl1.note || '').toUpperCase().includes('SUBSTITUTE_MEMBER')
                                                  ? '#92400E'
                                                  : '#6B7280',
                                                border: (lvl1.note || '').toUpperCase().includes('SUBSTITUTE_MEMBER')
                                                  ? '1px solid #F59E0B'
                                                  : '1px solid #D1D5DB',
                                              }}
                                              title="Bật/Tắt cơ chế Nhóm Nguyên Liệu Thay Thế (SUBSTITUTE_MEMBER)"
                                            >
                                              {(lvl1.note || '').toUpperCase().includes('SUBSTITUTE_MEMBER') ? '⇄ Bù trừ' : '⇄ Độc lập'}
                                            </button>

                                            {(lvl1.note || '').toUpperCase().includes('SUBSTITUTE_MEMBER') && (
                                              <div
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  backgroundColor: '#FFFBEB',
                                                  border: '1px solid #F59E0B',
                                                  borderRadius: 4,
                                                  padding: '1px 3px',
                                                }}
                                                title="Ngưỡng lệch chi phí tối đa cho phép khi bù trừ giữa các nguyên liệu trong BTP (VNĐ)"
                                              >
                                                <span style={{ fontSize: '9px', color: '#92400E', fontWeight: 600 }}>Lệch tối đa:</span>
                                                <input
                                                  type="number"
                                                  step="1000"
                                                  placeholder="25000"
                                                  value={((lvl1.note || '').match(/THRESHOLD[:=]\s*(\d+)/i) || [])[1] || ''}
                                                  onChange={(e) => {
                                                    const val = e.target.value.trim();
                                                    setBoms((prev) =>
                                                      prev.map((b) => {
                                                        if (
                                                          toComparisonKey(b.parent_name) === toComparisonKey(prod.name) &&
                                                          toComparisonKey(b.ingredient_name) === toComparisonKey(lvl1.ingredient_name)
                                                        ) {
                                                          const baseNote = (b.note || '').replace(/THRESHOLD[:=]\s*\d+/gi, '').replace(/\s+/g, ' ').trim();
                                                          return {
                                                            ...b,
                                                            note: val ? `${baseNote} THRESHOLD:${val}`.trim() : baseNote,
                                                          };
                                                        }
                                                        return b;
                                                      })
                                                    );
                                                    setIsDirty(true);
                                                  }}
                                                  style={{
                                                    width: 54,
                                                    fontSize: '10px',
                                                    padding: '0 2px',
                                                    borderRadius: 2,
                                                    border: '1px solid #D97706',
                                                    backgroundColor: '#FFF',
                                                    color: '#92400E',
                                                    fontWeight: 700,
                                                    textAlign: 'right',
                                                    height: 18,
                                                  }}
                                                />
                                                <span style={{ fontSize: '9px', color: '#92400E', fontWeight: 700 }}>đ</span>
                                              </div>
                                            )}
                                          </>
                                        )}
                                        {isLvl1Prep && (
                                          <button
                                            onClick={() => {
                                              const lvl1Canon = toComparisonKey(lvl1.ingredient_name);
                                              const existingBom = boms.find((b) => toComparisonKey(b.parent_name) === lvl1Canon);
                                              const pending = btpPendingYieldMap[lvl1Canon] || btpPendingYieldMap[lvl1.ingredient_name.toLowerCase().trim()];
                                              setShowAddChildModal({
                                                parentName: lvl1.ingredient_name,
                                                defaultYieldQty: existingBom?.yield_quantity ?? pending?.qty,
                                                defaultYieldUnit: existingBom?.yield_unit ?? pending?.unit,
                                              });
                                              setChildModalSelectedName('');
                                              setChildModalAmount(10);
                                              setChildModalUnit('g');
                                            }}
                                            className="action-icon-btn add-btn"
                                            title={`Thêm NL vào BTP "${lvl1.ingredient_name}"`}
                                          >
                                            <Plus size={14} />
                                          </button>
                                        )}
                                        <button
                                          onClick={() =>
                                            handleDeleteBomChild(prod.name, lvl1.ingredient_name)
                                          }
                                          className="action-icon-btn delete-btn"
                                          title={`Xóa ${lvl1.ingredient_name}`}
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>

                                  {/* LEVEL 2 CHILDREN (CỘT C: NGUYÊN LIỆU CỦA BTP) */}
                                  {isLvl1Prep &&
                                    (() => {
                                      const lvl1Canon = toComparisonKey(lvl1NameTrim);
                                      const btpBom = boms.find((b) => toComparisonKey(b.parent_name) === lvl1Canon);
                                      const pendingYield = btpPendingYieldMap[lvl1Canon] || btpPendingYieldMap[lvl1NameTrim.toLowerCase()];
                                      const btpYieldQty = (btpBom?.yield_quantity && btpBom.yield_quantity > 0)
                                        ? btpBom.yield_quantity
                                        : (pendingYield?.qty && pendingYield.qty > 0 ? pendingYield.qty : 1);
                                      const btpYieldUnit = btpBom?.yield_unit || pendingYield?.unit || lvl1Unit;

                                      return level2Children.map((lvl2) => {
                                        const lvl2Unit =
                                          unitMap.get(lvl2.ingredient_name.toLowerCase()) || 'g';
                                        const safeYield = (lvl2.yield_quantity && lvl2.yield_quantity > 0)
                                          ? lvl2.yield_quantity
                                          : (btpYieldQty > 0 ? btpYieldQty : 1);
                                        const rawScaled = (lvl2.amount * (Number(lvl1.amount) || 0)) / safeYield;
                                        const scaledAmount = Number(rawScaled.toFixed(2));

                                        return (
                                          <tr
                                            key={`bom-sub-${lvl1.ingredient_name}-${lvl2.ingredient_name}`}
                                            className="excel-level2-row"
                                          >
                                            {/* Col A indent */}
                                            <td className="cell-indent border-r" />
                                            {/* Col B indent */}
                                            <td className="cell-indent border-r" />
                                            {/* Col C content */}
                                            <td className="cell-level2-content border-r">
                                              <div className="subchild-item-flex" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span className="tree-subbranch-symbol">↳</span>
                                                <span className="subraw-dot">•</span>
                                                <span className="subchild-name">{lvl2.ingredient_name}</span>
                                                <span
                                                  style={{
                                                    fontSize: '9px',
                                                    color: '#065F46',
                                                    backgroundColor: '#ECFDF5',
                                                    border: '1px solid #A7F3D0',
                                                    borderRadius: '3px',
                                                    padding: '0 4px',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                  }}
                                                  title={`Tự động quy đổi tỷ lệ từ ${lvl1.amount} ${formatUnitDisplay(lvl1Unit)} BTP "${lvl1.ingredient_name}"`}
                                                >
                                                  Quy đổi BTP
                                                </span>
                                              </div>
                                            </td>
                                            {/* Amount */}
                                            <td className="cell-amount text-right border-r">
                                              <div
                                                style={{
                                                  display: 'inline-flex',
                                                  flexDirection: 'column',
                                                  alignItems: 'flex-end',
                                                  justifyContent: 'center',
                                                  padding: '2px 4px',
                                                }}
                                                title={`Tiêu hao thực tế cho ${lvl1.amount} ${formatUnitDisplay(lvl1Unit)} BTP "${lvl1.ingredient_name}" (tỷ lệ mẻ: ${lvl2.amount} / ${safeYield} ${formatUnitDisplay(btpYieldUnit)}).\nMuốn sửa công thức mẻ gốc, vui lòng sửa tại hàng BTP "${lvl1.ingredient_name}".`}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: '12.5px',
                                                    fontWeight: 700,
                                                    color: '#1E293B',
                                                    fontFamily: 'monospace',
                                                  }}
                                                >
                                                  {scaledAmount}
                                                </span>
                                                <span
                                                  style={{
                                                    fontSize: '9.5px',
                                                    color: '#6B7280',
                                                    whiteSpace: 'nowrap',
                                                    lineHeight: 1.1,
                                                  }}
                                                >
                                                  ({lvl2.amount}/{safeYield} {formatUnitDisplay(btpYieldUnit)})
                                                </span>
                                              </div>
                                            </td>
                                            <td className="cell-unit text-center border-r">
                                              <span className="unit-label">{formatUnitDisplay(lvl2Unit)}</span>
                                            </td>
                                          <td className="cell-actions text-center" style={{ minWidth: 90 }}>
                                            <div className="row-action-btns" style={{ justifyContent: 'center' }}>
                                              {(lvl2.note || '').toUpperCase().includes('SUBSTITUTE_MEMBER') ? (
                                                <span
                                                  style={{
                                                    fontSize: '10px',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    backgroundColor: '#FEF3C7',
                                                    color: '#92400E',
                                                    border: '1px solid #F59E0B',
                                                    fontWeight: 600,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                  }}
                                                  title={`Nguyên liệu con thuộc nhóm bù trừ được định nghĩa tại BTP "${lvl1.ingredient_name}"`}
                                                >
                                                  ⇄ Bù trừ
                                                </span>
                                              ) : (
                                                <span style={{ fontSize: '11px', color: '#9CA3AF' }}>—</span>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    });
                                  })()}
                                </React.Fragment>
                              );
                            })}

                          {/* Quick add prompt at bottom of product */}
                          {isExpanded && (
                            <tr className="excel-add-component-row">
                              <td className="cell-indent border-r" />
                              <td colSpan={5} className="cell-quick-add">
                                <button
                                  onClick={() => {
                                    setShowAddChildModal({
                                      parentName: prod.name,
                                      defaultYieldQty: prod.yieldQty,
                                      defaultYieldUnit: prod.yieldUnit,
                                    });
                                    setChildModalSelectedName('');
                                    setChildModalAmount(10);
                                    setChildModalUnit('g');
                                  }}
                                  className="quick-add-link"
                                >
                                  <Plus size={14} /> Thêm nguyên liệu / BTP vào món "{prod.name}"
                                </button>
                              </td>
                            </tr>
                          )}

                          {/* BOLD HORIZONTAL DIVIDER SEPARATING DISTINCT PRODUCTS */}
                          <tr className="excel-product-divider-row">
                            <td colSpan={6} />
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: KHO NGUYÊN LIỆU (02_NGUYEN_LIEU - CHỈ NGUYÊN LIỆU THÔ) */}
      {activeTab === 'INGREDIENTS' && (
        <div className="admin-content-area">
          <div className="filter-controls-row">
            <div className="search-box-wrapper" style={{ maxWidth: '100%' }}>
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Tìm tên nguyên liệu trong kho..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="column-card" style={{ width: '100%' }}>
            <div className="card-header-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 className="card-title">02. Danh Sách Nguyên Liệu Kho</h3>
                <span className="count-tag">
                  {ingredients.filter((i) => i.type !== 'PREPARED').length} nguyên liệu thô
                </span>
              </div>
              <button
                onClick={() => setShowAddIngredientModal(true)}
                className="btn-create-accent"
                style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                title="Thêm nguyên liệu mới vào kho 02"
              >
                <Plus size={15} />
                <span>Thêm Nguyên Liệu Mới</span>
              </button>
            </div>
            <div className="table-responsive">
              <table className="standard-admin-table">
                <thead>
                  <tr>
                    <th>Tên nguyên liệu</th>
                    <th>Nhóm hàng (00 Col B)</th>
                    <th>ĐVT chuẩn (00 Col C)</th>
                    <th className="text-right">Giá nhập (đ/ĐVT)</th>
                    <th className="text-right">Tồn quầy</th>
                    <th className="text-right">Tồn kho tổng</th>
                    <th className="text-right">Ngưỡng hao hụt (%) (02 Col F)</th>
                    <th className="text-center">Xóa</th>
                  </tr>
                </thead>
                <tbody>
                  {ingredients
                    .filter(
                      (ing) =>
                        ing.type !== 'PREPARED' &&
                        (!searchQuery || ing.name.toLowerCase().includes(searchQuery.toLowerCase()))
                    )
                    .map((ing) => (
                      <tr key={ing.name} className="hover-row">
                        <td className="font-medium">{ing.name}</td>
                        {/* Nhóm hàng Dropdown từ 00_Danh_muc Col B */}
                        <td>
                          <select
                            value={ing.category}
                            onChange={(e) => {
                              const newCat = e.target.value;
                              setIngredients((prev) =>
                                prev.map((i) => (i.name === ing.name ? { ...i, category: newCat } : i))
                              );
                              setIsDirty(true);
                            }}
                            className="table-dropdown-select"
                          >
                            {ingredientCategories.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </td>
                        {/* ĐVT Tiêu chuẩn Dropdown từ 00_Danh_muc Col C */}
                        <td>
                          <select
                            value={ing.unit}
                            onChange={(e) => {
                              const newUnit = e.target.value;
                              setIngredients((prev) =>
                                prev.map((i) => (toComparisonKey(i.name) === toComparisonKey(ing.name) ? { ...i, unit: newUnit } : i))
                              );
                              setConversions((prev) =>
                                prev.map((c) =>
                                  toComparisonKey(c.ingredient_name) === toComparisonKey(ing.name)
                                    ? { ...c, standard_unit: newUnit }
                                    : c
                                )
                              );
                              setIsDirty(true);
                            }}
                            className="table-dropdown-select font-mono"
                          >
                            {standardUnits.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                            {!standardUnits.includes(ing.unit) && (
                              <option key={ing.unit} value={ing.unit}>
                                {ing.unit}
                              </option>
                            )}
                          </select>
                        </td>
                        {/* Giá nhập nguyên liệu (cost_price) */}
                        <td className="text-right font-mono">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            placeholder="0"
                            value={ing.cost_price ?? ''}
                            onChange={(e) => {
                              const val = Math.max(0, parseFloat(e.target.value) || 0);
                              setIngredients((prev) =>
                                prev.map((i) =>
                                  i.name === ing.name ? { ...i, cost_price: val } : i
                                )
                              );
                              setIsDirty(true);
                            }}
                            className="table-inline-input"
                            title={`Giá nhập (VNĐ trên 1 ${ing.unit})`}
                            style={{ width: '100px', textAlign: 'right' }}
                          />
                        </td>
                        <td className="text-right font-mono">
                          <input
                            type="number"
                            value={ing.safety_stock_store ?? ing.safety_stock ?? 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setIngredients((prev) =>
                                prev.map((i) =>
                                  i.name === ing.name ? { ...i, safety_stock_store: val, safety_stock: val } : i
                                )
                              );
                              setIsDirty(true);
                            }}
                            className="table-inline-input"
                          />
                        </td>
                        <td className="text-right font-mono">
                          <input
                            type="number"
                            value={ing.safety_stock_central ?? 0}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setIngredients((prev) =>
                                prev.map((i) => (i.name === ing.name ? { ...i, safety_stock_central: val } : i))
                              );
                              setIsDirty(true);
                            }}
                            className="table-inline-input"
                          />
                        </td>
                        <td className="text-right font-mono">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="any"
                            value={ing.loss_tolerance_pct ?? 10}
                            onChange={(e) => {
                              const val = Math.max(0, parseFloat(e.target.value) || 0);
                              setIngredients((prev) =>
                                prev.map((i) => (i.name === ing.name ? { ...i, loss_tolerance_pct: val } : i))
                              );
                              setIsDirty(true);
                            }}
                            className="table-inline-input"
                            title="Ngưỡng hao hụt cho phép khi kiểm kê ca (%)"
                            style={{ width: '80px', textAlign: 'right' }}
                          />
                        </td>
                        <td className="text-center">
                          <button
                            onClick={() => {
                              setDeleteConfirmModal({
                                isOpen: true,
                                title: 'Xóa Nguyên Liệu Kho',
                                message: `Xác nhận xóa nguyên liệu "${ing.name}"? Các quy cách quy đổi liên quan trong 04_Quy_doi cũng sẽ được gỡ bỏ.`,
                                itemName: ing.name,
                                onConfirm: () => {
                                  setIngredients((prev) => prev.filter((i) => i.name !== ing.name));
                                  setConversions((prev) => prev.filter((c) => c.ingredient_name !== ing.name));
                                  setIsDirty(true);
                                  setDeleteConfirmModal(null);
                                  toast.success(`Đã xóa "${ing.name}"`);
                                },
                              });
                            }}
                            className="action-icon-btn delete-btn"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: QUY CÁCH ĐÓNG GÓI & QUY ĐỔI NHẬP HÀNG (04_QUY_DOI) */}
      {activeTab === 'CONVERSIONS' && (
        <div className="admin-content-area">
          <div className="filter-controls-row">
            <div className="search-box-wrapper" style={{ maxWidth: '100%' }}>
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Tìm quy cách quy đổi theo tên nguyên liệu hoặc ĐVT nhập..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="column-card" style={{ width: '100%' }}>
            <div className="card-header-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 className="card-title">04. Quy Cách Đóng Gói Nhập Hàng</h3>
                <span className="count-tag">{conversions.length} quy tắc</span>
              </div>
              <button
                onClick={() => setShowAddConversionModal(true)}
                className="btn-create-accent"
                style={{ padding: '6px 12px', fontSize: '0.8rem', backgroundColor: '#3C2114' }}
                title="Thêm quy cách đóng gói nhập hàng mới"
              >
                <Plus size={15} />
                <span>Thêm Quy Đổi Nhập Hàng</span>
              </button>
            </div>
            <div className="table-responsive">
              <table className="standard-admin-table">
                <thead>
                  <tr>
                    <th>Tên Nguyên Liệu (02 Col B)</th>
                    <th>ĐVT Nhập (00 Col D)</th>
                    <th className="text-right">Hệ số quy cách</th>
                    <th>ĐVT Chuẩn (Tự động)</th>
                    <th className="text-center">Xóa</th>
                  </tr>
                </thead>
                <tbody>
                  {conversions
                    .filter(
                      (c) =>
                        !searchQuery ||
                        c.ingredient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        c.package_unit.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((conv, idx) => {
                      // Tìm index thực tế trong mảng conversions gốc
                      const originalIndex = conversions.findIndex(
                        (c) =>
                          toComparisonKey(c.ingredient_name) === toComparisonKey(conv.ingredient_name) &&
                          toComparisonKey(c.package_unit) === toComparisonKey(conv.package_unit)
                      );
                      const targetIdx = originalIndex !== -1 ? originalIndex : idx;

                      return (
                      <tr key={`conv-${conv.ingredient_name}-${conv.package_unit}-${idx}`} className="hover-row">
                        <td className="font-medium" style={{ minWidth: 220 }}>
                          <ItemDropdown
                            value={conv.ingredient_name}
                            items={dropdownItemsForRaw}
                            placeholder="🔍 Tìm nguyên liệu..."
                            onChange={(newIng, unit) => {
                              const matched = ingredients.find(
                                (i) => toComparisonKey(i.name) === toComparisonKey(newIng)
                              );
                              setConversions((prev) =>
                                prev.map((c, i) =>
                                  i === targetIdx
                                    ? {
                                        ...c,
                                        ingredient_name: newIng,
                                        standard_unit: matched?.unit || unit || c.standard_unit,
                                      }
                                    : c
                                )
                              );
                              setIsDirty(true);
                            }}
                            style={{ minHeight: 34, height: 34, fontSize: 13 }}
                          />
                        </td>
                        <td>
                          <select
                            value={conv.package_unit}
                            onChange={(e) => {
                              const newPkg = e.target.value;
                              setConversions((prev) =>
                                prev.map((c, i) => (i === targetIdx ? { ...c, package_unit: newPkg } : c))
                              );
                              setIsDirty(true);
                            }}
                            className="table-dropdown-select font-bold"
                          >
                            {inwardUnits.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-right font-mono">
                          <input
                            type="number"
                            step="any"
                            value={conv.ratio === 0 ? '' : conv.ratio}
                            placeholder="0"
                            onChange={(e) => {
                              const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                              setConversions((prev) =>
                                prev.map((c, i) => (i === targetIdx ? { ...c, ratio: val } : c))
                              );
                              setIsDirty(true);
                            }}
                            className="table-inline-input"
                          />
                        </td>
                        <td className="font-mono text-tertiary">
                          <span className="auto-lookup-badge">
                            = {
                              ingredients.find(
                                (i) => toComparisonKey(i.name) === toComparisonKey(conv.ingredient_name)
                              )?.unit || conv.standard_unit
                            }
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            onClick={() => {
                              setDeleteConfirmModal({
                                isOpen: true,
                                title: 'Xóa Quy Cách Đóng Gói',
                                message: `Xác nhận xóa quy cách: 1 ${conv.package_unit} ${conv.ingredient_name}?`,
                                itemName: `${conv.ingredient_name} (${conv.package_unit})`,
                                onConfirm: () => {
                                  setConversions((prev) => prev.filter((_, i) => i !== targetIdx));
                                  setIsDirty(true);
                                  setDeleteConfirmModal(null);
                                  toast.success('Đã xóa quy cách quy đổi');
                                },
                              });
                            }}
                            className="action-icon-btn delete-btn"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: DANH MỤC TRUNG TÂM (00_DANH_MUC - THÊM / BỚT TRỰC TIẾP) */}
      {activeTab === 'CATEGORIES' && (
        <div className="admin-content-area">
          <div className="reference-info-banner">
            <Info size={18} />
            <span>
              Bộ từ điển nguồn trung tâm (Sheet <code>00_Danh_muc</code>). Chủ quán có thể <b>thêm mới hoặc xóa bớt</b> các mục bên dưới. Toàn bộ dropdown ở các tab khác sẽ tự động cập nhật theo!
            </span>
          </div>

          <div className="kanban-categories-grid">
            {/* Cột 1: Nhóm Món */}
            <div className="kanban-col-card">
              <div className="kanban-header-flex">
                <h4 className="kanban-col-title">Cột A: Nhóm Món</h4>
                <span className="count-tag">{productCategories.length}</span>
              </div>
              <div className="category-add-row">
                <input
                  type="text"
                  placeholder="+ Thêm nhóm món..."
                  value={newCatInput['PROD_CAT'] || ''}
                  onChange={(e) => setNewCatInput({ ...newCatInput, PROD_CAT: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategoryItem('PROD_CAT')}
                  className="cat-quick-input"
                />
                <button onClick={() => handleAddCategoryItem('PROD_CAT')} className="cat-quick-add-btn">
                  <Plus size={14} />
                </button>
              </div>
              <div className="kanban-item-list">
                {productCategories.map((item) => (
                  <div key={item} className="kanban-pill-item">
                    <span>{item}</span>
                    <button
                      onClick={() => handleRemoveCategoryItem('PROD_CAT', item)}
                      className="cat-item-remove-btn"
                      title="Xóa nhóm này"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cột 2: Nhóm Hàng */}
            <div className="kanban-col-card">
              <div className="kanban-header-flex">
                <h4 className="kanban-col-title">Cột B: Nhóm Hàng Kho</h4>
                <span className="count-tag">{ingredientCategories.length}</span>
              </div>
              <div className="category-add-row">
                <input
                  type="text"
                  placeholder="+ Thêm nhóm hàng..."
                  value={newCatInput['ING_CAT'] || ''}
                  onChange={(e) => setNewCatInput({ ...newCatInput, ING_CAT: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategoryItem('ING_CAT')}
                  className="cat-quick-input"
                />
                <button onClick={() => handleAddCategoryItem('ING_CAT')} className="cat-quick-add-btn">
                  <Plus size={14} />
                </button>
              </div>
              <div className="kanban-item-list">
                {ingredientCategories.map((item) => (
                  <div key={item} className="kanban-pill-item">
                    <span>{item}</span>
                    <button
                      onClick={() => handleRemoveCategoryItem('ING_CAT', item)}
                      className="cat-item-remove-btn"
                      title="Xóa nhóm này"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cột 3: ĐVT Tiêu Chuẩn */}
            <div className="kanban-col-card">
              <div className="kanban-header-flex">
                <h4 className="kanban-col-title">Cột C: ĐVT Tiêu Chuẩn</h4>
                <span className="count-tag">{standardUnits.length}</span>
              </div>
              <div className="category-add-row">
                <input
                  type="text"
                  placeholder="+ Thêm ĐVT..."
                  value={newCatInput['STD_UNIT'] || ''}
                  onChange={(e) => setNewCatInput({ ...newCatInput, STD_UNIT: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategoryItem('STD_UNIT')}
                  className="cat-quick-input font-mono"
                />
                <button onClick={() => handleAddCategoryItem('STD_UNIT')} className="cat-quick-add-btn">
                  <Plus size={14} />
                </button>
              </div>
              <div className="kanban-item-list">
                {standardUnits.map((item) => (
                  <div key={item} className="kanban-pill-item font-mono">
                    <span>{item}</span>
                    <button
                      onClick={() => handleRemoveCategoryItem('STD_UNIT', item)}
                      className="cat-item-remove-btn"
                      title="Xóa ĐVT này"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cột 4: ĐVT Nhập NCC */}
            <div className="kanban-col-card">
              <div className="kanban-header-flex">
                <h4 className="kanban-col-title">Cột D: ĐVT Nhập NCC</h4>
                <span className="count-tag">{inwardUnits.length}</span>
              </div>
              <div className="category-add-row">
                <input
                  type="text"
                  placeholder="+ Thêm ĐVT nhập..."
                  value={newCatInput['INWARD_UNIT'] || ''}
                  onChange={(e) => setNewCatInput({ ...newCatInput, INWARD_UNIT: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategoryItem('INWARD_UNIT')}
                  className="cat-quick-input"
                />
                <button onClick={() => handleAddCategoryItem('INWARD_UNIT')} className="cat-quick-add-btn">
                  <Plus size={14} />
                </button>
              </div>
              <div className="kanban-item-list">
                {inwardUnits.map((item) => (
                  <div key={item} className="kanban-pill-item font-bold">
                    <span>{item}</span>
                    <button
                      onClick={() => handleRemoveCategoryItem('INWARD_UNIT', item)}
                      className="cat-item-remove-btn"
                      title="Xóa ĐVT này"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: THÊM MÓN BÁN MỚI (PREPEND LÊN ĐẦU DANH SÁCH) */}
      {showAddProductModal && (
        <div className="modal-backdrop">
          <div className="modal-dialog-card">
            <h3 className="modal-title">Thêm Món Mới Vào Menu</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Món mới sẽ được đưa lên ngay đầu danh sách để bạn dễ dàng nhìn thấy và tiếp tục thêm công thức định mức.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const name = (form.elements.namedItem('prodName') as HTMLInputElement).value;
                const category = (form.elements.namedItem('prodCat') as HTMLSelectElement).value;
                const price = parseFloat((form.elements.namedItem('prodPrice') as HTMLInputElement).value) || 0;
                handleCreateProduct(name, category, price);
              }}
            >
              <div className="form-group">
                <label className="form-label">Tên món bán:</label>
                <input
                  name="prodName"
                  required
                  placeholder="Ví dụ: Cà phê muối, Trà đào cam sả..."
                  className="modal-input"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Nhóm món <span className="label-badge">00 Col A</span>:
                </label>
                <select name="prodCat" className="modal-input">
                  {productCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Giá bán (VNĐ):</label>
                <input
                  name="prodPrice"
                  type="number"
                  defaultValue={35000}
                  className="modal-input font-mono"
                />
              </div>

              <div className="modal-actions-bar">
                <button
                  type="button"
                  onClick={() => setShowAddProductModal(false)}
                  className="btn-admin-secondary"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-admin-primary">
                  Tạo Món (Lên đầu danh sách)
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 1B: THÊM BÁN THÀNH PHẨM (BTP) MỚI */}
      {showAddBtpModal && (
        <div className="modal-backdrop">
          <div className="modal-dialog-card">
            <h3 className="modal-title">⭐ Thêm Bán Thành Phẩm Mới (BTP)</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Khai báo BTP (Cà phê phin, Cốt trà, Kem muối, Thạch,...) và sản lượng định mức của 1 mẻ ủ/nấu để tự động tính hao hụt khi chốt ca.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const name = (form.elements.namedItem('btpName') as HTMLInputElement).value;
                const rawYield = parseFloat((form.elements.namedItem('btpYield') as HTMLInputElement).value);
                const yieldQty = isNaN(rawYield) || rawYield <= 0 ? 1000 : rawYield;
                const yieldUnit = (form.elements.namedItem('btpUnit') as HTMLSelectElement).value || 'g';
                handleCreateBtp(name, yieldQty, yieldUnit);
              }}
            >
              <div className="form-group">
                <label className="form-label">Tên Bán Thành Phẩm (BTP) (*):</label>
                <input
                  name="btpName"
                  required
                  placeholder="Ví dụ: Cà phê phin TP, Cốt lục trà sữa TP..."
                  className="modal-input"
                  autoFocus
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Sản lượng 1 mẻ thu được (Yield) (*):</label>
                  <input
                    name="btpYield"
                    type="number"
                    step="any"
                    required
                    defaultValue={1000}
                    className="modal-input font-mono"
                    placeholder="1000"
                  />
                  {/* Nút bấm nhanh chọn sản lượng mẻ (100, 200, 500, 1000, 2000) */}
                  <div style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
                    {[100, 200, 500, 1000, 1500, 2000].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={(ev) => {
                          const f = (ev.target as HTMLElement).closest('form');
                          const inp = f?.elements.namedItem('btpYield') as HTMLInputElement;
                          if (inp) inp.value = String(preset);
                        }}
                        style={{
                          fontSize: 11,
                          padding: '2px 7px',
                          borderRadius: 4,
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-surface-subtle)',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Đơn vị thu được (*):</label>
                  <select name="btpUnit" className="modal-input font-mono" defaultValue="g">
                    <option value="g">g (gram)</option>
                    <option value="ml">ml (mili lít)</option>
                    <option value="unit">unit (phần)</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions-bar">
                <button
                  type="button"
                  onClick={() => setShowAddBtpModal(false)}
                  className="btn-admin-secondary"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-admin-primary" style={{ background: 'var(--amber-700, #b45309)' }}>
                  Tạo BTP & Thêm Thành Phần
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: THÊM THÀNH PHẦN CON VÀO MÓN (CHUẨN FORMAT L1: ItemDropdown + Stepper + Quick Presets) */}
      {showAddChildModal && (
        <div className="modal-backdrop">
          <div className="modal-dialog-card modal-l1-style">
            <div className="modal-l1-header">
              <div className="l1-icon-badge">
                <PackagePlus size={20} />
              </div>
              <div>
                <h3 className="modal-title" style={{ margin: 0 }}>
                  Thêm Thành Phần Vào "{showAddChildModal.parentName}"
                </h3>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  Chọn nguyên liệu / BTP và chỉnh định lượng với công cụ chọn nhanh
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddChildModal(null)}
                className="close-modal-x"
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
              {/* 1. Chọn thành phần qua ItemDropdown chuẩn L1 */}
              <div>
                <label className="form-label" style={{ marginBottom: 6 }}>
                  1. Chọn nguyên liệu hoặc BTP (Có tìm kiếm thông minh) (*)
                </label>
                <ItemDropdown
                  value={childModalSelectedName}
                  items={dropdownItemsForBom}
                  placeholder="🔍 Gõ để tìm nguyên liệu thô hoặc BTP..."
                  onChange={(name, unit) => {
                    setChildModalSelectedName(name);
                    setChildModalUnit(unit || 'g');
                  }}
                  style={{ minHeight: 46, height: 46, fontSize: 14 }}
                />
              </div>

              {/* 2. Stepper chọn số lượng định mức chuẩn L1 */}
              <div>
                <label className="form-label" style={{ marginBottom: 6 }}>
                  2. Định lượng cần dùng ({childModalUnit}) (*)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="l1-stepper-box">
                    <button
                      type="button"
                      onClick={() => setChildModalAmount((prev) => Math.max(1, prev - 1))}
                      className="stepper-btn"
                      title="Giảm 1"
                    >
                      <Minus size={18} />
                    </button>
                    <input
                      type="number"
                      step="any"
                      min="0.1"
                      value={childModalAmount === 0 ? '' : childModalAmount}
                      onChange={(e) => setChildModalAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="stepper-input num"
                    />
                    <button
                      type="button"
                      onClick={() => setChildModalAmount((prev) => prev + 1)}
                      className="stepper-btn"
                      title="Tăng 1"
                    >
                      <Plus size={18} />
                    </button>
                  </div>

                  <div className="unit-display-badge">
                    {childModalUnit}
                  </div>
                </div>

                {/* Quick preset increments */}
                <div className="presets-row">
                  {[5, 10, 15, 20, 30, 40, 50, 60, 100].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setChildModalAmount(preset)}
                      className={`preset-chip ${childModalAmount === preset ? 'active' : ''}`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions-bar" style={{ marginTop: 22 }}>
              <button
                type="button"
                onClick={() => setShowAddChildModal(null)}
                className="btn-admin-secondary"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleAddChildToBomConfirm}
                disabled={!childModalSelectedName || childModalAmount <= 0}
                className="btn-admin-primary"
              >
                Thêm Vào Công Thức
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: THÊM NGUYÊN LIỆU MỚI (CHỈ NGUYÊN LIỆU THÔ) */}
      {showAddIngredientModal && (
        <div className="modal-backdrop">
          <div className="modal-dialog-card">
            <h3 className="modal-title">Thêm Nguyên Liệu Thô Vào Kho</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Chỉ khai báo nguyên liệu thô nhập từ nhà cung cấp. Bán thành phẩm (BTP) được quản lý tự động qua công thức pha chế.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const name = (form.elements.namedItem('ingName') as HTMLInputElement).value.trim();
                const category = (form.elements.namedItem('ingCat') as HTMLSelectElement).value;
                const unit = (form.elements.namedItem('ingUnit') as HTMLSelectElement).value;
                const safety_store = parseFloat((form.elements.namedItem('ingStore') as HTMLInputElement).value) || 0;
                const safety_central = parseFloat((form.elements.namedItem('ingCentral') as HTMLInputElement).value) || 0;
                const cost_price = parseFloat((form.elements.namedItem('ingCost') as HTMLInputElement).value) || 0;
                const tolerance = parseFloat((form.elements.namedItem('ingTolerance') as HTMLInputElement)?.value) || 10;

                if (!name) return;
                if (ingredients.some((i) => i.name.toLowerCase() === name.toLowerCase())) {
                  toast.error(`Nguyên liệu "${name}" đã tồn tại!`);
                  return;
                }

                setIngredients((prev) => [
                  ...prev,
                  {
                    name,
                    category,
                    type: 'RAW',
                    unit,
                    safety_stock: safety_store,
                    safety_stock_store: safety_store,
                    safety_stock_central: safety_central,
                    loss_tolerance_pct: tolerance,
                    cost_price,
                  },
                ]);
                setIsDirty(true);
                setShowAddIngredientModal(false);
                toast.success(`Đã thêm nguyên liệu "${name}"`);
              }}
            >
              <div className="form-group">
                <label className="form-label">Tên nguyên liệu:</label>
                <input
                  name="ingName"
                  required
                  placeholder="Ví dụ: Cà phê hạt Robusta, Sữa đặc..."
                  className="modal-input"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Nhóm hàng <span className="label-badge">00 Col B</span>:
                </label>
                <select name="ingCat" className="modal-input">
                  {ingredientCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Đơn vị tính chuẩn <span className="label-badge">00 Col C</span>:
                </label>
                <select name="ingUnit" className="modal-input font-mono">
                  {standardUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Giá nhập đơn vị (VNĐ/ĐVT):</label>
                <input
                  name="ingCost"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Ví dụ: 70 (cho 70đ/g sữa lon), 45 (cho 45đ/g kem thùng)..."
                  className="modal-input font-mono"
                />
              </div>

              <div className="form-row-2">
                <div className="form-group flex-1">
                  <label className="form-label">Tồn an toàn Quầy:</label>
                  <input
                    name="ingStore"
                    type="number"
                    defaultValue={1000}
                    className="modal-input font-mono"
                  />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">Tồn an toàn Kho Tổng:</label>
                  <input
                    name="ingCentral"
                    type="number"
                    defaultValue={5000}
                    className="modal-input font-mono"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Ngưỡng hao hụt (%) <span className="label-badge">02 Col F</span>:
                </label>
                <input
                  name="ingTolerance"
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  defaultValue={10}
                  className="modal-input font-mono"
                />
              </div>

              <div className="modal-actions-bar">
                <button
                  type="button"
                  onClick={() => setShowAddIngredientModal(false)}
                  className="btn-admin-secondary"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-admin-primary">
                  Thêm Vào Kho
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: THÊM QUY CÁCH ĐÓNG GÓI NHẬP HÀNG (04_Quy_doi) */}
      {showAddConversionModal && (
        <div className="modal-backdrop">
          <div className="modal-dialog-card">
            <h3 className="modal-title">Thêm Quy Cách Đóng Gói Nhập Hàng</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const ingName = convModalSelectedIng.trim();
                const pkgUnit = (form.elements.namedItem('convPkg') as HTMLSelectElement).value;
                const ratio = parseFloat((form.elements.namedItem('convRatio') as HTMLInputElement).value) || 0;

                if (!ingName || !pkgUnit || ratio <= 0) {
                  toast.error('Vui lòng chọn nguyên liệu và nhập hệ số quy cách > 0');
                  return;
                }

                const exists = conversions.some(
                  (c) => c.ingredient_name.toLowerCase() === ingName.toLowerCase() && c.package_unit.toLowerCase() === pkgUnit.toLowerCase()
                );
                if (exists) {
                  toast.error(`Quy cách ${pkgUnit} cho "${ingName}" đã tồn tại!`);
                  return;
                }

                const matchedIng = ingredients.find(
                  (i) => toComparisonKey(i.name) === toComparisonKey(ingName)
                );
                const rawStd = matchedIng?.unit || 'g';
                const stdUnit = (!rawStd || rawStd.toLowerCase() === 'unit') ? 'g' : rawStd;

                setConversions((prev) => [
                  ...prev,
                  {
                    ingredient_name: ingName,
                    package_unit: pkgUnit,
                    ratio,
                    standard_unit: stdUnit,
                  },
                ]);
                setIsDirty(true);
                setShowAddConversionModal(false);
                setConvModalSelectedIng('');
                toast.success(`Đã thêm quy cách 1 ${pkgUnit} = ${ratio} ${stdUnit}`);
              }}
            >
              <div className="form-group">
                <label className="form-label">
                  Nguyên liệu <span className="label-badge">02 Col B</span> (*):
                </label>
                <ItemDropdown
                  value={convModalSelectedIng}
                  items={dropdownItemsForRaw}
                  placeholder="🔍 Gõ tìm tên nguyên liệu..."
                  onChange={(name) => setConvModalSelectedIng(name)}
                  style={{ minHeight: 46, height: 46, fontSize: 14 }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Đơn vị tính đóng gói NCC <span className="label-badge">00 Col D</span>:
                </label>
                <select name="convPkg" className="modal-input font-bold">
                  {inwardUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Hệ số quy đổi (1 ĐVT Nhập = bao nhiêu ĐVT Chuẩn):</label>
                <input
                  name="convRatio"
                  type="number"
                  step="any"
                  required
                  placeholder="Ví dụ: 1000, 24, 1284..."
                  className="modal-input font-mono"
                />
              </div>

              <div className="modal-actions-bar">
                <button
                  type="button"
                  onClick={() => setShowAddConversionModal(false)}
                  className="btn-admin-secondary"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-admin-primary">
                  Thêm Quy Cách
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL XÁC NHẬN XÓA (CUSTOM CONFIRM MODAL - THAY THẾ HOÀN TOÀN confirm() CỦA JS) */}
      {deleteConfirmModal?.isOpen && (
        <div className="modal-backdrop">
          <div className="modal-dialog-card modal-delete-confirm">
            <div className="modal-delete-icon-wrapper">
              <Trash2 size={24} />
            </div>
            <h3 className="modal-title" style={{ textAlign: 'center', margin: '12px 0 8px 0', color: '#DC2626' }}>
              {deleteConfirmModal.title}
            </h3>
            <p className="modal-delete-message">
              {deleteConfirmModal.message}
            </p>
            {deleteConfirmModal.itemName && (
              <div className="modal-delete-target-pill">
                Mục chọn: <b>{deleteConfirmModal.itemName}</b>
              </div>
            )}
            <div className="modal-actions-bar" style={{ justifyContent: 'center', marginTop: 22, gap: 12 }}>
              <button
                type="button"
                onClick={() => setDeleteConfirmModal(null)}
                className="btn-admin-secondary"
                style={{ minWidth: 100, padding: '10px 16px' }}
              >
                Hủy bỏ
              </button>
              <button
                type="button"
                onClick={deleteConfirmModal.onConfirm}
                className="btn-delete-confirm"
                style={{ minWidth: 120, padding: '10px 18px' }}
              >
                Xác nhận xóa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CẢNH BÁO / HỎI LƯU THAY ĐỔI KHI CHUYỂN TAB TRÌNH DUYỆT */}
      {showUnsavedModal && isDirty && (
        <div className="modal-backdrop">
          <div className="modal-dialog-card" style={{ maxWidth: 440, textAlign: 'center', padding: '24px 20px' }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                backgroundColor: '#FEF3C7',
                color: '#D97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px auto',
              }}
            >
              <AlertTriangle size={28} />
            </div>
            <h3 className="modal-title" style={{ fontSize: '1.15rem', marginBottom: 8 }}>
              Bạn có thay đổi Master Data chưa lưu!
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              Hệ thống phát hiện bạn vừa chuyển tab hoặc rời màn hình trong khi dữ liệu bảng Master Data đã được chỉnh sửa. Bạn có muốn lưu lại ngay bây giờ không?
            </p>
            <div className="modal-actions-bar" style={{ justifyContent: 'center', gap: 12 }}>
              <button
                type="button"
                onClick={() => setShowUnsavedModal(false)}
                className="btn-admin-secondary"
                style={{ minWidth: 110, padding: '10px 16px' }}
              >
                Tiếp tục sửa
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowUnsavedModal(false);
                  await handleSaveMaster();
                }}
                disabled={saving}
                className="btn-floating-save"
                style={{ minWidth: 130, padding: '10px 18px' }}
              >
                {saving ? (
                  <>
                    <div className="btn-spinner" />
                    <span>Đang lưu...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Lưu ngay</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FLOATING ACTION DOCK - GHIM NÚT LƯU KHI CUỘN NỘI DUNG DÀI */}
      {isDirty && (
        <div className="master-floating-dock">
          <div className="floating-dock-info">
            <span className="floating-dock-badge" />
            <span>Có thay đổi chưa lưu</span>
          </div>
          <button
            onClick={handleSaveMaster}
            disabled={saving}
            className="btn-floating-save"
            title="Lưu các thay đổi Master Data ngay lập tức"
          >
            {saving ? (
              <>
                <div className="btn-spinner" />
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>LƯU MASTER DATA</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
